import { Copy, Check, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NextWaveTechWidget from '@/components/NextWaveTechWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function WidgetPage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const widgetCode = `<div id="nwth-widget-root"></div>
<script>
  (function() {
    var container = document.createElement('div');
    container.id = 'nwth-widget-container';
    document.body.appendChild(container);
    
    var script = document.createElement('script');
    script.src = '${BACKEND_URL}/widget.js';
    script.async = true;
    script.onload = function() {
      if (window.NextWaveTechWidget) {
        window.NextWaveTechWidget.init('nwth-widget-container');
      }
    };
    document.body.appendChild(script);
  })();
</script>`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(widgetCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen">
      <div className="grid-background absolute inset-0 opacity-20"></div>

      {/* Header */}
      <nav className="relative z-10 border-b border-[#00f0ff]/30 bg-[#0a0a1f]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#00f0ff] via-[#ff00ff] to-[#b026ff] text-transparent bg-clip-text">
              Embeddable Widget
            </h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 border border-[#00f0ff] text-[#00f0ff] rounded-lg hover:bg-[#00f0ff] hover:text-[#0a0a1f] transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Demo Section */}
        <div className="glass rounded-xl p-8 glow-box mb-8">
          <h2 className="text-3xl font-bold text-[#00f0ff] glow mb-4">
            📱 Live Widget Demo
          </h2>
          <p className="text-[#00f0ff]/70 mb-6">
            Look at the bottom-right corner of this page! That's your embeddable widget in action. Click it to see how customers will interact with it.
          </p>
          <div className="p-4 bg-[#ff00ff]/10 border border-[#ff00ff] rounded-lg">
            <p className="text-[#ff00ff] font-bold mb-2">✨ Widget Features:</p>
            <ul className="text-[#00f0ff] space-y-2">
              <li>🤖 Real-time AI chat assistant</li>
              <li>⚡ Instant responses to customer questions</li>
              <li>💬 Beautiful chat interface with synthwave theme</li>
              <li>🎯 Quick action buttons for common issues</li>
              <li>📱 Mobile-responsive design</li>
              <li>🔓 No login required - instant help</li>
            </ul>
          </div>
        </div>

        {/* Installation Instructions */}
        <div className="glass rounded-xl p-8 glow-box-magenta mb-8">
          <h2 className="text-3xl font-bold text-[#ff00ff] glow mb-6">
            🚀 How to Install on Squarespace
          </h2>

          {/* Step 1 */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-[#00f0ff] mb-3">
              Step 1: Copy the Widget Code
            </h3>
            <div className="relative">
              <pre className="p-4 bg-[#0a0a1f] border border-[#00f0ff] rounded-lg overflow-x-auto">
                <code className="text-[#ff00ff] text-sm">{widgetCode}</code>
              </pre>
              <button
                onClick={copyToClipboard}
                className="absolute top-4 right-4 px-4 py-2 bg-[#00f0ff] text-[#0a0a1f] font-bold rounded-lg flex items-center space-x-2"
              >
                {copied ? <Check size={20} /> : <Copy size={20} />}
                <span>{copied ? 'Copied!' : 'Copy Code'}</span>
              </button>
            </div>
          </div>

          {/* Step 2 */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-[#00f0ff] mb-3">
              Step 2: Add to Squarespace
            </h3>
            <ol className="list-decimal list-inside space-y-3 text-[#00f0ff]/80">
              <li>Log in to your Squarespace dashboard at <strong className="text-[#ff00ff]">nextwavetechhelp.squarespace.com</strong></li>
              <li>Navigate to <strong className="text-[#ff00ff]">Settings → Advanced → Code Injection</strong></li>
              <li>Paste the widget code in the <strong className="text-[#ff00ff]">FOOTER</strong> section</li>
              <li>Click <strong className="text-[#ff00ff]">Save</strong></li>
              <li>The widget will now appear on all pages of your website!</li>
            </ol>
          </div>

          {/* Step 3 */}
          <div className="p-4 bg-[#00ff00]/10 border border-[#00ff00] rounded-lg">
            <h3 className="text-lg font-bold text-[#00ff00] mb-2">✅ That's It!</h3>
            <p className="text-[#00f0ff]/80">
              The widget will automatically appear on your Squarespace site. Your visitors can click it to request services, and you'll receive email notifications for every inquiry!
            </p>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="glass rounded-xl p-8 glow-box-purple mb-8">
          <h2 className="text-3xl font-bold text-[#b026ff] glow mb-6">
            🎯 Customer Journey
          </h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00f0ff] flex items-center justify-center font-bold text-[#0a0a1f]">1</div>
              <div>
                <h4 className="font-bold text-[#00f0ff] mb-1">Customer Clicks Widget</h4>
                <p className="text-[#00f0ff]/70">They see the pulsing chat button on your website</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#ff00ff] flex items-center justify-center font-bold text-white">2</div>
              <div>
                <h4 className="font-bold text-[#ff00ff] mb-1">AI Greets Them</h4>
                <p className="text-[#00f0ff]/70">Instant welcome message from AI assistant</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#b026ff] flex items-center justify-center font-bold text-white">3</div>
              <div>
                <h4 className="font-bold text-[#b026ff] mb-1">Real-Time Chat</h4>
                <p className="text-[#00f0ff]/70">AI answers questions about services, pricing, and troubleshooting</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00ff00] flex items-center justify-center font-bold text-[#0a0a1f]">4</div>
              <div>
                <h4 className="font-bold text-[#00ff00] mb-1">Quick Actions</h4>
                <p className="text-[#00f0ff]/70">Customers can use quick buttons for common issues</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00f0ff] flex items-center justify-center font-bold text-[#0a0a1f]">5</div>
              <div>
                <h4 className="font-bold text-[#00f0ff] mb-1">24/7 Availability</h4>
                <p className="text-[#00f0ff]/70">AI handles simple requests anytime, even when you're away</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#ff00ff] flex items-center justify-center font-bold text-white">6</div>
              <div>
                <h4 className="font-bold text-[#ff00ff] mb-1">Escalation</h4>
                <p className="text-[#00f0ff]/70">For complex issues, AI suggests creating a ticket or booking a service</p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="glass rounded-xl p-6 glow-box">
            <h3 className="text-xl font-bold text-[#00f0ff] mb-4">💡 Pro Tips</h3>
            <ul className="space-y-2 text-[#00f0ff]/80">
              <li>• Widget works on all pages automatically</li>
              <li>• Mobile-responsive and touch-friendly</li>
              <li>• Won't interfere with Squarespace features</li>
              <li>• Loads asynchronously (won't slow your site)</li>
              <li>• Matches your synthwave branding</li>
            </ul>
          </div>
          <div className="glass rounded-xl p-6 glow-box-magenta">
            <h3 className="text-xl font-bold text-[#ff00ff] mb-4">📧 Email Notifications</h3>
            <ul className="space-y-2 text-[#00f0ff]/80">
              <li>• You receive every widget submission</li>
              <li>• Customer gets confirmation email</li>
              <li>• All emails branded with your colors</li>
              <li>• Links back to customer portal</li>
              <li>• No spam - only real inquiries</li>
            </ul>
          </div>
        </div>

        {/* Test Widget */}
        <div className="mt-8 text-center">
          <p className="text-[#00f0ff]/70 mb-4">
            Test the widget on this page by clicking the button in the bottom-right corner! →
          </p>
        </div>
      </div>

      {/* Render the actual widget */}
      <NextWaveTechWidget />
    </div>
  );
}

export default WidgetPage;
